import gql from "graphql-tag";

import { setContext } from "@apollo/client/link/context";
import { getTokenInfo } from "contexts/AuthManager/Auth";
import { TRACING_HEADERS } from "../constants/constants";
import { DEFAULT_OPTIONS } from "./providerUtils";
import { getShouldAddTracingHeaderToRequests } from "components/Settings/settingsUtils";
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import {
  ASKR_CUSTOM_URL_LS_KEY,
  ASKR_INGESTR_CUSTOM_URL_LS_KEY,
} from "App/CustomAskrUrlIndicator";

const ASKR = "askr";
const ASKR_INGESTR = "askr_ingestr";

const askrAuthLink = setContext(() => {
  const { idToken } = getTokenInfo();

  const shouldAddTracingHeaders = getShouldAddTracingHeaderToRequests();

  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      authorization: idToken ? `Bearer ${idToken}` : "no-token",
      ...(shouldAddTracingHeaders ? TRACING_HEADERS : {}),
    },
  };
});

class Clients {
  service: string;
  constructor(service: string) {
    this.service = service;
  }
  private clients_ = new Map<string, any>();

  get(ckey: string) {
    const c = this.clients_.get(ckey);
    if (c) {
      return c;
    }

    const customAskrUrl = window.localStorage.getItem(ASKR_CUSTOM_URL_LS_KEY);
    const customAskrIngestrUrl = window.localStorage.getItem(
      ASKR_INGESTR_CUSTOM_URL_LS_KEY
    );
    if (this.service === ASKR && customAskrUrl) {
      console.log("⚠🚨 ~ using custom Askr url:", customAskrUrl);
    } else if (this.service === ASKR_INGESTR && customAskrIngestrUrl) {
      console.log(
        "⚠🚨 ~ using custom Askr Ingestr url:",
        customAskrIngestrUrl
      );
    }

    const cluster = "gke-us-east1";
    const uri =
      (this.service === ASKR
        ? customAskrUrl
        : this.service === ASKR_INGESTR
          ? customAskrIngestrUrl
          : false) ||
      `https://${ckey}.${cluster}.raven-cloud.com/${this.service}/graphql/`;

    // link to use if not batching
    const httpLink = new HttpLink({
      uri,
    });

    const httpLinkWithAuth = ApolloLink.from([askrAuthLink.concat(httpLink)]);

    this.clients_.set(
      ckey,
      new ApolloClient({
        cache: new InMemoryCache(),
        link: httpLinkWithAuth,
        defaultOptions: DEFAULT_OPTIONS,
        connectToDevTools: process.env.NODE_ENV === "development",
      })
    );
    // ? Given that we just set this key, can this return undefined?
    return this.clients_.get(ckey);
  }
}

export const askrClients = new Clients(ASKR);
export const askrIngestrClients = new Clients(ASKR_INGESTR);

export const PRODUCTION_SEGMENTS_QUERY = gql`
  query ProductionSegments(
    $machemetrySourceName: String!
    $ge: String!
    $lt: String!
  ) {
    productionSegments(
      process: { machemetrySourceName: $machemetrySourceName }
      range: { ge: $ge, lt: $lt }
    ) @connection(key: "productionSegments", filter: ["process"]) {
      tag {
        name
      }
      pnl {
        category
      }
      range {
        ge
        lt
      }
      meta {
        explanations
        traceability
      }
      events {
        ts
        ... on TagEvent {
          operationId
          datasourceGroup {
            type
            name
          }
        }
      }
    }
  }
`;

export const PRODUCTION_SEGMENTS_QUERY_FILTERED = gql`
  query ProductionSegments(
    $machemetrySourceName: String!
    $ge: String!
    $lt: String!
    $datasourceGroupIds: String!
  ) {
    productionSegments(
      process: { machemetrySourceName: $machemetrySourceName }
      range: { ge: $ge, lt: $lt }
      filter: { input: { datasourceGroupIds: $datasourceGroupIds } }
    ) @connection(key: "productionSegments", filter: ["process"]) {
      tag {
        name
      }
      pnl {
        category
      }
      range {
        ge
        lt
      }
      meta {
        explanations
        traceability
      }
      events {
        ts
        ... on TagEvent {
          operationId
          datasourceGroup {
            type
            name
          }
        }
      }
    }
  }
`;

export const GET_PRODUCTION_AGGREGATES_QUERY = ({
  willAggregateOnFrequency,
  isShiftly = false,
}: {
  willAggregateOnFrequency: boolean;
  isShiftly?: boolean;
  // tslint:disable-next-line: no-big-function
}) =>
  willAggregateOnFrequency && isShiftly
    ? gql`
        query ProductionAggregatesWithFrequency(
          $machemetrySourceName: String!
          $ge: String!
          $lt: String!
        ) {
          productionAggregates(
            process: { machemetrySourceName: $machemetrySourceName }
            range: { ge: $ge, lt: $lt }
            aggregateOnFrequency: { type: SHIFTLY }
          ) @connection(key: "productionAggregates", filter: ["process"]) {
            asOf
            shift {
              name
            }
            notScheduled {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            planned {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            producing {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            uncollected {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            unknown {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            unplanned {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            waiting {
              totalDurationMs
              tags {
                totalDurationMs
                tag {
                  name
                  path
                }
              }
            }
            totalDurationMs
            range {
              ge
              lt
            }
          }
        }
      `
    : willAggregateOnFrequency && !isShiftly
      ? gql`
          query ProductionAggregatesWithFrequency(
            $machemetrySourceName: String!
            $ge: String!
            $lt: String!
            $aggregateOnFrequencyType: FrequencyType!
            $startingOnMinute: Int = 0
          ) {
            productionAggregates(
              process: { machemetrySourceName: $machemetrySourceName }
              range: { ge: $ge, lt: $lt }
              aggregateOnFrequency: {
                type: $aggregateOnFrequencyType
                startingOn: { minute: $startingOnMinute }
              }
            ) @connection(key: "productionAggregates", filter: ["process"]) {
              asOf
              shift {
                name
              }
              notScheduled {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              planned {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              producing {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              uncollected {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              unknown {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              unplanned {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              waiting {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              totalDurationMs
              range {
                ge
                lt
              }
            }
          }
        `
      : // !willAggregateOnFrequency
        gql`
          query ProductionAggregates(
            $machemetrySourceName: String!
            $ge: String!
            $lt: String!
          ) {
            productionAggregates(
              process: { machemetrySourceName: $machemetrySourceName }
              range: { ge: $ge, lt: $lt }
            ) @connection(key: "productionAggregates", filter: ["process"]) {
              asOf
              shift {
                name
              }
              notScheduled {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              planned {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              producing {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              uncollected {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              unknown {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              unplanned {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              waiting {
                totalDurationMs
                tags {
                  totalDurationMs
                  tag {
                    name
                    path
                  }
                }
              }
              totalDurationMs
              range {
                ge
                lt
              }
            }
          }
        `;

export const SHIFT_SEGMENTS_QUERY = gql`
  query ShiftSegments(
    $ge: String!
    $lt: String!
    $processName: String
    $machemetrySourceName: String
  ) {
    shiftSegments(
      range: { ge: $ge, lt: $lt }
      process: {
        machemetrySourceName: $machemetrySourceName
        name: $processName
      }
    ) @connection(key: "shiftSegments", filter: []) {
      shift {
        name
        workScheduleId
      }
      range {
        ge
        lt
      }
    }
  }
`;

/** get the Midgard id associated with a Machemetry id */
export const PROCESS_QUERY = gql`
  query Process($machemetryId: String!) {
    process(byMachemetrySourceName: $machemetryId) {
      id
    }
  }
`;

export const APPLY_RETROACTIVE_TAG = gql`
  mutation ApplyRetroactiveTag(
    $id: String!
    $ge: String!
    $lt: String!
    $tagPath: [String]
  ) {
    applyRetroactiveTag(
      process: { id: $id }
      range: { ge: $ge, lt: $lt }
      tag: { path: $tagPath }
    )
  }
`;

export const DELETE_OPERATION = gql`
  mutation DeleteOperation($operationId: String!) {
    deleteOperation(operation: { id: $operationId })
  }
`;
